import React, { useState, useEffect } from 'react'
import { FaTimes, FaCheck } from 'react-icons/fa'
import { useParams } from 'react-router'
import { Box, Stack, Loading, SimpleTable, SimpleMenu, Button, IconButton, Tag, Text, Select, Alert, Input, ImageViewer } from '@/components/lib'
import utils, { likertAnswers, likertStatements, likertTotal, ratingLabelFor } from '@/utils/utils'
import api from '../../../api'
import useStore from '../../../store'
import EmptyBox from '../../includes/EmptyBox'

import BMITag from '../../includes/BMITag'

export default function Explore () {
  const [dataTypeFilter, setDataTypeFilter] = useState('')
  const [results, setResults] = useState([])
  const [columns, setColumns] = useState([])
  const [group, setGroup] = useState('')
  const [stage, setStage] = useState('')
  const [dataset, setDataset] = useState('stage')
  const [stageRecurrence] = useState()
  const [loading, setLoading] = useState(false)
  const { id } = useParams()
  const { trial, groups: availableGroups, stages, dataTypes } = useStore()
  const trialId = id

  useEffect(() => {
    if (!trialId || !stage) return
    setLoading(true)
    api.trials.getResponsesExplore(trialId, { dataset, columns, group, stage }, r => {
      setResults(r.results)
      setLoading(false)
    })
  }, [trialId, columns, group, stage, dataset])

  // Auto-select the first stage in the list
  useEffect(() => {
    if (stage) return
    if (stages?.length) {
      setStage(stages[0]._id)
    }
  }, [stages])

  // Auto-select first few data types
  useEffect(() => {
    if (dataTypes?.length === 0) return
    if (columns?.length > 0) return
    const newColumns = []
    dataTypes.slice(0, 4)?.map(d => newColumns.push(d._id))
    setColumns(['createdAt', 'lastSeenAt'])
  }, [dataTypes])

  // Build list of columns
  const availableColumns = [
    { key: 'createdAt', name: 'Signed-up', format: v => utils.formatDateTime(v) },
    { key: 'lastSeenAt', name: 'Last seen', format: v => utils.formatDateTime(v) }
  ]
  dataTypes?.forEach(d => {
    const col = { key: d._id, name: d.name }
    if (d.type === 'likert') {
      // A matrix has no single value, so it contributes a column per statement
      // plus the total. The suffix after `::` keeps their keys unique for
      // add/remove; the API resolves the data type from the part before it.
      likertStatements(d).forEach(statement => {
        availableColumns.push({
          key: `${d._id}::${statement.key}`,
          name: `${d.name} — ${statement.label}`,
          format: r => {
            const answer = likertAnswers(d, r?.value).find(a => a.key === statement.key)
            return <Text>{answer ? answer.answerLabel : ''}</Text>
          }
        })
      })
      availableColumns.push({
        key: `${d._id}::__total`,
        name: `${d.name} — total`,
        format: r => <Text>{likertTotal(r?.value, d) ?? ''}</Text>
      })
      return
    }
    if (d.type === 'text' || d.type === 'paragraph' || d.type === 'integer' || d.type === 'float' || d.type === 'slider') {
      col.format = r => <Text>{r?.value}</Text>
    } else if (d.type === 'rating') {
      // The number is what was stored and what any analysis works from, so it
      // leads; the label follows it for the same reason it does in the response
      // viewer — "4" alone doesn't say how long the scale was or what it meant.
      col.format = r => {
        if (r?.value === null || r?.value === undefined) return <Text />
        const label = ratingLabelFor(d, r.value)
        return <Text>{label ? `${r.value} — ${label}` : r.value}</Text>
      }
    } else if (d.type === 'choice') {
      col.format = r => {
        const rVal = Array.isArray(r?.value) ? r?.value : (r?.value ? [r?.value] : null)
        return rVal?.map(v => <Tag key={v} size='sm' colorPalette='blue' mr={1} mb={1}>{v}</Tag>)
      }
    } else if (d.type === 'date') {
      col.format = r => <Text>{r?.value && utils.formatDate(r.value)}</Text>
    } else if (d.type === 'datetime') {
      col.format = r => <Text>{r?.value && utils.formatDateTime(r.value)}</Text>
    } else if (d.type === 'time') {
      col.format = r => <Text>{r?.value && utils.formatTime(r.value)}</Text>
    } else if (d.type === 'food') {
      col.format = r => <Text>{r?.value?.amountConsumed}g {r?.value?.name}</Text>
    } else if (d.type === 'image') {
      col.format = (r, p) => <ImageViewer url={r?.value?.url} withPreview />
    } else if (d.type === 'temperature') {
      col.format = r => <Text>{!isNaN(r?.value) ? `${parseFloat(r?.value)?.toFixed(2)} °C` : ''}</Text>
    } else if (d.type === 'weight') {
      col.format = r => <Text>{!isNaN(r?.value) ? `${parseFloat(r?.value)?.toFixed(2)} kg` : ''}</Text>
    } else if (d.type === 'height') {
      col.format = r => <Text>{!isNaN(r?.value) ? `${parseFloat(r?.value)?.toFixed(2)} cm` : ''}</Text>
    } else if (d.type === 'bmi') {
      col.format = r => <BMITag bmi={r?.value?.bmi} />
    } else if (d.type === 'calculation') {
      col.format = r => <Text>{typeof r?.value === 'number' ? r.value : (r?.value != null ? String(r.value) : '')}</Text>
    } else return // Otherwise don't include in column list
    availableColumns.push(col)
  })

  const removeColumn = key => {
    const index = columns.indexOf(key)
    if (index > -1) {
      const newColumns = Object.assign([], columns)
      newColumns.splice(index, 1)
      setColumns(newColumns)
    }
  }
  const addColumn = key => {
    const index = columns.indexOf(key)
    if (index === -1) {
      const newColumns = Object.assign([], columns)
      newColumns.push(key)
      setColumns(newColumns)
    }
  }

  const filteredAvailableColumns = dataTypeFilter
    ? availableColumns?.filter(c => c.name.toLowerCase().indexOf(dataTypeFilter.toLowerCase()) > -1)
    : availableColumns

  return (
    <Stack gap={5}>
      <Stack direction='row' alignItems='center'>
        {availableGroups?.length > 0 &&
          <Select
            placeholder='All participants' maxW={200} value={group} size='sm'
            onChange={e => setGroup(e.target.value)}
          >
            {availableGroups?.map(g => <option value={g._id} key={g._id}>{g.name}</option>)}
          </Select>}
        {trial.garminEnabled &&
          <Select placeholder='Select a dataset...' maxW={200} value={dataset} size='sm' onChange={e => setDataset(e.target.value)}>
            <option value='stage'>Core staged data</option>
            <option value='hardwareDaily'>Wearable daily data</option>
            <option value='hardwareSleep'>Wearable sleep data</option>
            <option value='hardwareActivity'>Wearable activity data</option>
          </Select>}
        {stages?.length > 0 && dataset === 'stage' &&
          <Select
            placeholder='Select a stage...' w={300} size='sm'
            value={`${stage}${stageRecurrence ? `::${stageRecurrence}` : ''}`}
            onChange={e => setStage(e.target.value)}
          >
            {stages?.map(s => {
              return s.isRecurring
                ? Array.from({ length: s.recurringDuration }, (_, i) => i)?.map(recurrence =>
                  <option value={`${s._id}::${recurrence}`} key={`${s._id}::${recurrence}`}>{s.name} ({recurrence + 1})</option>)
                : <option value={s._id} key={s._id}>{s.name}</option>
            })}
          </Select>}
      </Stack>

      {loading && <Box m={5} textAlign='center'><Loading /></Box>}
      {!loading && !results?.length &&
        <EmptyBox
          icon='notfound' title={'There isn\'t any data here yet'}
          description='Data will appear here when responses to your stages are recorded.'
        />}
      {!loading && results?.length > 0 &&
        <Stack>
          {columns?.length > 4 &&
            <Alert fontSize='sm' variant='left-accent'>If you want to view lots of columns, we recommend exporting your trial data and analysing it with a dedicated tool, such as Excel.</Alert>}

          {dataset === 'stage' &&
            <SimpleTable
              asCard
              tableProps={{ variant: 'striped', size: 'sm' }}
              headers={[
                'Participant'
              ].concat(columns?.map(col => {
                const definition = availableColumns?.find(c => c.key === col)
                return (
                  <Box key={col} display='flex' alignItems='center'>
                    {definition?.name}
                    <IconButton colorPalette='blue' size='xs' variant='ghost' onClick={() => removeColumn(col)} icon={FaTimes} />
                  </Box>
                )
              })).concat([
                <Box display='flex' justifyContent='end' key='add'>
                  <SimpleMenu
                    trigger={<Button size='sm' colorPalette='primary' variant='solid'>Add columns...</Button>}
                    items={[
                      { type: 'component', component: <Box p={2}><Input value={dataTypeFilter} onChange={e => setDataTypeFilter(e.target.value)} autoFocus size='xs' placeholder='Search data types...' /></Box> }
                    ].concat(filteredAvailableColumns?.map(col => {
                      const alreadySelected = columns.indexOf(col.key) > -1
                      const action = alreadySelected ? removeColumn : addColumn
                      return {
                        type: 'item',
                        icon: alreadySelected ? FaCheck : null,
                        text: col.name,
                        onClick: () => action(col.key)
                      }
                    }))}
                  />
                </Box>
              ])}
              rows={results?.map(result => {
                const cells = [result.participant?.id]
                columns?.forEach(col => {
                  const definition = availableColumns?.find(c => c.key === col)
                  const val = result[col]
                  const formatted = definition?.format ? definition.format(val, result.participant) : JSON.stringify(val)
                  cells.push(formatted)
                })
                return cells
              })}
            />}

          {dataset === 'hardwareDaily' &&
            <SimpleTable
              tableProps={{ variant: 'striped', size: 'sm' }}
              headers={['Participant', 'Date', 'Active calories', 'Base calories', 'Steps', 'Distance (m)', 'Floors', 'Avg heart rate', 'Avg stress']}
              rows={results?.map(result => [
                result.participantId,
                result.date,
                result.activeCalories,
                result.baseCalories,
                result.steps,
                result.distance,
                result.floorsClimbed,
                result.averageHeartRate,
                result.averageStress
              ])}
            />}

          {dataset === 'hardwareSleep' &&
            <SimpleTable
              tableProps={{ variant: 'striped', size: 'sm' }}
              headers={['Participant', 'Date', 'Duration', 'Nap duration', 'Deep sleep', 'Light sleep', 'REM sleep', 'Awake', 'Score']}
              rows={results?.map(result => [
                result.participantId,
                result.date,
                result.duration,
                result.napDuration,
                result.deepSleepDuration,
                result.lightSleepDuration,
                result.remSleepDuration,
                result.awakeDuration,
                result.sleepScore?.value
              ])}
            />}

          {dataset === 'hardwareActivity' &&
            <SimpleTable
              tableProps={{ variant: 'striped', size: 'sm' }}
              headers={['Participant', 'Date', 'Type', 'Duration', 'Distance (m)', 'Average speed (m/s)', 'Max speed (m/s)', 'Calories', 'Steps']}
              rows={results?.map(result => [
                result.participantId,
                utils.formatDateTime(result.datetime),
                result.type,
                result.duration,
                result.distance,
                result.averageSpeed,
                result.maxSpeed,
                result.activeCalories,
                result.steps
              ])}
            />}
        </Stack>}
    </Stack>
  )
}
